import React, { useRef } from "react"

import classNames from "classnames"

import { generateUUID } from "shared/lib/generateUUID"
import { getNextSortOrder } from "shared/lib/sort-order/getNextSortOrder"
import { useEvent } from "shared/lib/useEvent"
import { IconPlacement } from "shared/types/Button"
import { TableSorting } from "shared/types/DataTable"
import { SortOrder } from "shared/types/SortOrder"
import { DataTableCell } from "shared/types/__generated__/graphql"
import { Button } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"

interface DataTableHeaderProps {
  header: DataTableCell[] | null | undefined
  className?: string
  sorting?: TableSorting | null
  isSortable?: boolean
  onSortByColumn?: (sorting: TableSorting) => void
}

interface DataTableHeaderCellProps {
  cell: DataTableCell
  sortOrder?: SortOrder | null
  isSortable?: boolean
  onSortByColumn?: (sorting: TableSorting) => void
}

const DataTableHeaderCell = React.memo(
  ({
    cell,
    sortOrder,
    isSortable,
    onSortByColumn
  }: DataTableHeaderCellProps) => {
    const handleClick = useEvent(() => {
      if (cell.key) {
        onSortByColumn?.({
          key: cell.key,
          sortOrder: getNextSortOrder(sortOrder)
        })
      }
    })

    if (!isSortable) {
      return (
        <th scope="col" className={classNames(cell.className)} key={cell.value}>
          <div className="data-table__cell-content">{cell.value}</div>
        </th>
      )
    }

    return (
      <th scope="col" className={classNames(cell.className)} key={cell.value}>
        <Button
          className="data-table__cell-content"
          onClick={handleClick}
          icon={
            sortOrder
              ? (`sort-${sortOrder.toLocaleLowerCase()}` as IconName)
              : undefined
          }
          iconPlacement={IconPlacement.RIGHT}
        >
          {cell.value}
        </Button>
      </th>
    )
  }
)

export const DataTableHeader = React.memo(
  ({ header, className, sorting, ...rest }: DataTableHeaderProps) => {
    const id = useRef(generateUUID())

    if (!header || header.length === 0) {
      return null
    }

    return (
      <thead className={classNames("data-table__header", className)}>
        <tr data-testid="data-table-header">
          {header?.map((cell, index) => (
            <DataTableHeaderCell
              cell={cell}
              key={cell.key || `header-cell-${id}-${index}`}
              sortOrder={
                cell.key === sorting?.key ? sorting?.sortOrder : undefined
              }
              {...rest}
            />
          ))}
        </tr>
      </thead>
    )
  }
)
